import { useState, useEffect, useRef, useCallback } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  HStack,
  FormControl,
  Input,
  Button,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
  Badge,
  Select,
  Text,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";

import api from "./api";
import {
  CopyIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";

type Platform = "all" | "gmx" | "hyperliquid";

interface AdminOrder {
  id: string;
  platform: "gmx" | "hyperliquid";
  address: string;
  txHash?: string;
  market?: string | null;
  symbol?: string | null;
  side?: string;
  sizeUsd: number;
  pending: boolean;
  fills?: number | null;
  time: string;
}

interface PlatformTotals {
  orders: number;
  volume: number;
  pending: number;
}

interface Summary {
  totalOrders: number;
  totalVolume: number;
  pendingOrders: number;
  byPlatform: Record<"gmx" | "hyperliquid", PlatformTotals>;
}

const DATE_PRESETS = [7, 14, 30, 90] as const;
type DatePreset = (typeof DATE_PRESETS)[number] | null;

// Local calendar date (YYYY-MM-DD) for <input type="date">
function formatDateForInput(date: Date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getDateRangeFromPreset(days: number) {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  return {
    startDate: formatDateForInput(start),
    endDate: formatDateForInput(end),
  };
}

// The inputs hold local dates; the API wants exact instants covering whole local days
function toRangeParams(range: { startDate: string; endDate: string }) {
  const params: Record<string, string> = {};
  if (range.startDate) {
    params.startDate = new Date(`${range.startDate}T00:00:00`).toISOString();
  }
  if (range.endDate) {
    params.endDate = new Date(`${range.endDate}T23:59:59.999`).toISOString();
  }
  return params;
}

const formatUsd = (value: number) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const txUrl = (order: AdminOrder) =>
  order.platform === "hyperliquid"
    ? `https://app.hyperliquid.xyz/explorer/tx/${order.txHash}`
    : `https://arbiscan.io/tx/${order.txHash}`;

const shorten = (value: string) => `${value.slice(0, 6)}...${value.slice(-4)}`;

function Orders() {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [platform, setPlatform] = useState<Platform>("all");
  const [sortConfig, setSortConfig] = useState<{
    key: "time" | "amount";
    direction: "asc" | "desc";
  }>({ key: "time", direction: "desc" });
  const [dateRange, setDateRange] = useState(getDateRangeFromPreset(90));
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>(90);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;
  const requestId = useRef(0);

  const hasMore = orders.length < total;

  // Debounce search so typing does not fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchOrders = useCallback(
    async (page: number) => {
      const id = ++requestId.current;
      if (page === 1) setLoading(true);
      else setLoadingMore(true);
      setError("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pageSize),
          platform,
          sortBy: sortConfig.key,
          sortOrder: sortConfig.direction,
          ...toRangeParams(dateRange),
          ...(searchTerm ? { search: searchTerm } : {}),
        });
        const res = await api.get(`/api/admin/orders/all?${params}`);
        // Drop responses from filters the user has already changed
        if (id !== requestId.current) return;
        const data = res?.data?.data;
        const newOrders: AdminOrder[] = data?.orders || [];
        setOrders((prev) => (page === 1 ? newOrders : [...prev, ...newOrders]));
        setSummary(data?.summary || null);
        setTotal(data?.pagination?.total ?? 0);
      } catch (err: any) {
        if (id !== requestId.current) return;
        setError(err?.response?.data?.message || "Error loading orders");
      } finally {
        if (id === requestId.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [platform, sortConfig, dateRange, searchTerm]
  );

  // Any filter change restarts from page 1
  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1);
  }, [fetchOrders]);

  useEffect(() => {
    if (currentPage > 1) fetchOrders(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading || loadingMore || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 200) {
        setCurrentPage((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loading, loadingMore, hasMore]);

  const handleSort = (key: "time" | "amount") => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc",
    }));
  };

  const sortIcon = (key: "time" | "amount") =>
    sortConfig.key === key &&
    (sortConfig.direction === "asc" ? <ChevronUpIcon /> : <ChevronDownIcon />);

  const copy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied!`);
  };

  const filtersActive =
    !!searchInput || !!dateRange.startDate || !!dateRange.endDate || platform !== "all";

  const summaryCard = (
    label: string,
    value: React.ReactNode,
    detail: React.ReactNode,
    color: string
  ) => (
    <Box
      p={5}
      bg="white"
      borderRadius="lg"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="sm"
      borderLeftWidth="4px"
      borderLeftColor={color}
    >
      <Box
        fontSize="xs"
        color="gray.500"
        textTransform="uppercase"
        letterSpacing="wide"
        fontWeight="semibold"
      >
        {label}
      </Box>
      <Box fontSize="2xl" fontWeight="bold" color="gray.800" mt={1}>
        {loading && !summary ? <Spinner size="sm" /> : value}
      </Box>
      {summary && (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {detail}
        </Text>
      )}
    </Box>
  );

  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Orders
        </Heading>

        <HStack spacing={4} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search address, tx hash, market"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          <Select
            maxW="170px"
            value={platform}
            onChange={(e) => setPlatform(e.target.value as Platform)}
          >
            <option value="all">All platforms</option>
            <option value="gmx">GMX</option>
            <option value="hyperliquid">Hyperliquid</option>
          </Select>

          <FormControl maxW="200px">
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, startDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>

          <FormControl maxW="200px">
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => {
                setDateRange({ ...dateRange, endDate: e.target.value });
                setSelectedPreset(null);
              }}
            />
          </FormControl>

          <HStack spacing={2}>
            {DATE_PRESETS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={selectedPreset === days ? "solid" : "outline"}
                colorScheme={selectedPreset === days ? "blue" : "gray"}
                onClick={() => {
                  setDateRange(getDateRangeFromPreset(days));
                  setSelectedPreset(days);
                }}
              >
                {days}d
              </Button>
            ))}
          </HStack>

          {filtersActive && (
            <Button
              size="sm"
              onClick={() => {
                setSearchInput("");
                setPlatform("all");
                setDateRange({ startDate: "", endDate: "" });
                setSelectedPreset(null);
                setSortConfig({ key: "time", direction: "desc" });
              }}
            >
              Clear Filters
            </Button>
          )}
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {summaryCard(
            "Total Orders",
            (summary?.totalOrders ?? 0).toLocaleString(),
            <>
              GMX {summary?.byPlatform.gmx.orders.toLocaleString()} · Hyperliquid{" "}
              {summary?.byPlatform.hyperliquid.orders.toLocaleString()}
            </>,
            "blue.400"
          )}
          {summaryCard(
            "Total Volume",
            formatUsd(summary?.totalVolume ?? 0),
            <>
              GMX {formatUsd(summary?.byPlatform.gmx.volume ?? 0)} · Hyperliquid{" "}
              {formatUsd(summary?.byPlatform.hyperliquid.volume ?? 0)}
              {!!summary?.pendingOrders && " · unfilled limit orders excluded"}
            </>,
            "green.400"
          )}
        </SimpleGrid>

        {loading && orders.length === 0 && <PageLoader message="Loading orders..." />}

        <SimpleGrid columns={{ base: 1, md: 1 }} spacing={8}>
          {error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : (
            <Table
              variant="simple"
              sx={{
                "@media (max-width: 768px)": {
                  display: "block",
                  overflowX: "auto",
                  tr: {
                    display: "block",
                    marginBottom: "1rem",
                    borderBottom: "2px solid #ddd",
                  },
                  td: {
                    display: "block",
                    textAlign: "right",
                    paddingLeft: "50%",
                    position: "relative",
                    "&::before": {
                      content: `attr(data-label)`,
                      position: "absolute",
                      left: 0,
                      width: "50%",
                      paddingLeft: "1rem",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                  },
                  th: {
                    display: "none",
                  },
                },
              }}
            >
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <Th>Platform</Th>
                  <Th>Address</Th>
                  <Th>Tx Hash</Th>
                  <Th>Market</Th>
                  <Th>Side</Th>
                  <Th cursor="pointer" onClick={() => handleSort("amount")}>
                    <HStack spacing={1}>
                      <span>Amount</span>
                      {sortIcon("amount")}
                    </HStack>
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort("time")}>
                    <HStack spacing={1}>
                      <span>Time</span>
                      {sortIcon("time")}
                    </HStack>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {orders.map((order, idx) => (
                  <Tr key={`${order.platform}-${order.id}`}>
                    <Td data-label="#">{idx + 1}</Td>
                    <Td data-label="Platform">
                      <Badge colorScheme={order.platform === "gmx" ? "blue" : "teal"}>
                        {order.platform === "gmx" ? "GMX" : "Hyperliquid"}
                      </Badge>
                    </Td>
                    <Td data-label="Address">
                      {order.address ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <span>{shorten(order.address)}</span>
                          <CopyIcon
                            cursor="pointer"
                            onClick={() => copy(order.address, "Address")}
                          />
                        </Box>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td data-label="Tx Hash">
                      {order.txHash ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <a
                            href={txUrl(order)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#63b3ed" }}
                          >
                            {shorten(order.txHash)} ↗
                          </a>
                          <CopyIcon
                            cursor="pointer"
                            onClick={() => copy(order.txHash!, "Tx hash")}
                          />
                        </Box>
                      ) : (
                        <span style={{ color: "#718096" }}>—</span>
                      )}
                    </Td>
                    <Td data-label="Market">
                      {order.symbol && !order.symbol.startsWith("0x") ? (
                        <strong>{order.symbol}</strong>
                      ) : order.market ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <span>{shorten(order.market)}</span>
                          <CopyIcon
                            cursor="pointer"
                            onClick={() => copy(order.market!, "Market")}
                          />
                        </Box>
                      ) : (
                        "—"
                      )}
                    </Td>
                    <Td data-label="Side">{order.side || "—"}</Td>
                    <Td data-label="Amount">
                      {formatUsd(order.sizeUsd)}
                      {order.pending && (
                        <Badge ml={2} colorScheme="yellow">
                          Not filled
                        </Badge>
                      )}
                      {!!order.fills && order.fills > 1 && (
                        <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                          {order.fills} fills
                        </Text>
                      )}
                    </Td>
                    <Td data-label="Time">
                      {order.time ? new Date(order.time).toLocaleString() : ""}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}

          {loadingMore && (
            <Box textAlign="center" py={4}>
              <Spinner size="md" />
              <Box mt={2}>Loading more orders...</Box>
            </Box>
          )}

          {!loading && !error && orders.length === 0 && (
            <Box textAlign="center" py={4} color="gray.500">
              No orders match these filters
            </Box>
          )}

          {!hasMore && orders.length > 0 && (
            <Box textAlign="center" py={4} color="gray.500">
              No more orders to load
            </Box>
          )}
        </SimpleGrid>
      </VStack>
    </Container>
  );
}

export default Orders;
